import Breadcrumbs from "@/components/Breadcrumbs";
import MuiLoadingSwitch from "@/components/MuiLoadingSwitch";
import { SettingsService } from "@/services/admin-services";
import { SettingsLanguage } from "@/services/admin-services/models";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
    Alert,
    AlertColor,
    Box,
    CircularProgress,
    Collapse,
    Divider,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

const breadcrumbs = [
    {
        to: "../..",
        title: "Administration",
    },
    {
        title: "Settings",
        to: "../..",
    },
    {
        title: "Languages",
        to: "",
    },
];

const Languages = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<string>("");
    const [data, setData] = useState<SettingsLanguage[]>([]);
    const [notification, setNotification] = useState<{
        open: boolean;
        message?: string;
        type: AlertColor;
    }>({ open: false, type: "success" });

    const notificationTimerRef = useRef<NodeJS.Timeout>();

    const settingsService = useMemo(() => new SettingsService("int"), []);

    useEffect(() => {
        settingsService.getLanguages().then((data) => {
            setData(data);
            setIsLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!notification.open) return;

        notificationTimerRef.current = setTimeout(() => hideNotification(), 3000);
    }, [notification]);

    const displayNotification = (message: string, type: AlertColor) => setNotification({ open: true, message, type });
    const hideNotification = () => setNotification({ open: false, type: "success" });

    const updateStatus = (slug: string, status: boolean) => {
        const language = data.find((language) => language.slug === slug);
        if (!language) return;

        setIsSaving(slug);
        settingsService
            .updateLanguage(slug, {
                ...language,
                active: status,
            })
            .then((result) => {
                // Find the index of the updated language by slug in the data array and replace it
                const index = data.findIndex((language) => language.slug === slug);
                if (index === -1) return;

                displayNotification(
                    `Succesfully ${status ? "activated" : "disabled"} the language ${language.label}.`,
                    "success"
                );

                setData((data) => {
                    const newData = [...data];
                    newData[index] = result;
                    return newData;
                });
                setIsSaving("");
            });
    };

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbs} />

            <Divider />

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Box sx={{ width: "100%", ml: 2 }}>
                    <Collapse in={notification.open}>
                        <Alert
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => hideNotification()}
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                            severity={notification.type}
                        >
                            {notification.message}
                        </Alert>
                    </Collapse>
                </Box>

                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Language
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 100 }}>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Active
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    data.map((item, i) => (
                                        <TableRow key={i} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                            <TableCell component="th" scope="row">
                                                <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                    {item.label}
                                                </Typography>
                                            </TableCell>
                                            <TableCell component="td" scope="row">
                                                <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                    <MuiLoadingSwitch
                                                        loading={isSaving === item.slug}
                                                        checked={item.active}
                                                        onChange={() => updateStatus(item.slug, !item.active)}
                                                        disabled={Boolean(isSaving)}
                                                    />
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Languages;
